import React from 'react'
import { useIntl } from 'react-intl'
import { TableContainer, Table, TableHead, TableBody } from '@mui/material'
import { TableRow, TableCell, Typography, CircularProgress, Box } from '@mui/material'
import { withStyles } from '@mui/styles'

import { basicListStyles } from './basic-list.styles'

/**
 * @typedef {object} BasicListColumn
 * @property {string} key - La clave del campo en los datos que se mostrará en esta columna.
 * @property {string} label - El texto que se mostrará en el encabezado de la columna.
 * @property {'left' | 'center' | 'right'} [align='left'] - La alineación del contenido de la columna.
 * @property {'date' | 'datetime'} [type] - El tipo de dato para aplicar formato automático. Si es `date`, formatea como fecha; si es `datetime`, formatea como fecha y hora.
 */

/**
 * @typedef {object} BasicListProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {BasicListColumn[]} config - Array de configuración de columnas que define qué campos mostrar y cómo formatearlos.
 * @property {object[]} data - Array de objetos con los datos a mostrar en la tabla.
 * @property {boolean} [loading=false] - Si es `true`, muestra un indicador de carga en lugar de la tabla.
 */

/**
 * Un componente de lista básica que renderiza datos en formato de tabla con soporte para formateo automático de fechas.
 * 
 * Utiliza Material-UI Table para mostrar datos de forma estructurada. Soporta formateo automático de fechas y fechas con hora
 * mediante react-intl. Cuando está en estado de carga, muestra un spinner centrado en lugar del contenido.
 * 
 * El componente formatea automáticamente los valores según el tipo especificado en la configuración de columnas,
 * aplicando las preferencias de localización del usuario para fechas y horas.
 * 
 * @component
 * @example
 * const MyDataList = () => {
 *   const [loading, setLoading] = useState(true);
 *   const [items, setItems] = useState([]);
 * 
 *   const columns = [
 *     { key: 'nombre', label: 'Nombre', align: 'left' },
 *     { key: 'cantidad', label: 'Cantidad', align: 'right' },
 *     { key: 'fecha_creacion', label: 'Fecha de Creación', type: 'date' },
 *     { key: 'ultima_modificacion', label: 'Última Modificación', type: 'datetime' },
 *   ];
 * 
 *   useEffect(() => {
 *     fetchData().then((data) => {
 *       setItems(data);
 *       setLoading(false);
 *     });
 *   }, []);
 * 
 *   return <BasicList config={columns} data={items} loading={loading} />;
 * };
 * 
 * @param {BasicListProps} props - Las props del componente BasicList.
 */
export const BasicList = withStyles(basicListStyles)(({
  classes,
  config,
  data,
  loading = false,
}) => {
  const { formatDate, formatTime } = useIntl()
  const getParsedValue = (type, value) => {
    switch (type) {
      case 'date':
        return formatDate(new Date(value))
      case 'datetime':
        return formatDate(new Date(value)) + ' - ' + formatTime(new Date(value))
      default:
        return value
    }
  }

  return loading ? (
    <Box display="flex" alignItems="center" justifyContent="center" flexGrow={2}>
      <CircularProgress size={30} />
    </Box>
  ) : (
    <TableContainer>
      <Table className={classes.table} aria-label="simple table">
        <TableHead>
          <TableRow>
            {config.map(({ align = 'left', label }, i) => (
              <TableCell key={i} align={align}>
                <Typography variant="subtitle1">{label}</Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={i}>
              {config.map(({ key, align = 'left', type }, i) => (
                <TableCell key={i} align={align}>
                  <Typography variant="body2" color="textSecondary">
                    {getParsedValue(type, row[key])}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
})
